import { Button, ButtonsGroup, Modal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useSegment } from '../../../../../../hooks'
import { getOrganizationViewUrl } from '../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { BlockCancelInvoiceActionVariant } from '../../../BlockCancelInvoiceActionModal/enums/blockCancelInvoiceActionVariant'
import { useCancelInvoiceModal } from '../../contexts/cancelInvoiceModalContext'

export const CancelInvoiceModalFooter = (): ReactElement | null => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const { track } = useSegment()

  const {
    closeCancelInvoiceModal,
    invoiceId,
    isCancelBlockedByFinalInvoice,
    isCreateCreditNoteAction,
    isDeleteInvoiceAction,
    isDeletingInvoice,
    handleDeleteInvoice,
    onConfirmCreditNote,
    openBlockCancelInvoiceActionModal,
    shouldDisplayCancelInvoiceModalFooter,
    trackingDocumentType,
  } = useCancelInvoiceModal()

  const handleCancelWithCreditNoteButtonClick = useCallback(() => {
    track('XXX - Invoicing Core - Cancel invoice modal - Yes selected - Cancel with credit note clicked', {
      documentType: trackingDocumentType,
    })

    if (isCancelBlockedByFinalInvoice) {
      openBlockCancelInvoiceActionModal(BlockCancelInvoiceActionVariant.CreditNote)
      return
    }

    closeCancelInvoiceModal()
    if (onConfirmCreditNote) {
      onConfirmCreditNote()
    } else {
      navigate(
        getOrganizationViewUrl(
          organization?.url,
          reactRoute.organization.invoices.getCreditNoteFromInvoiceRoute(invoiceId),
        ),
      )
    }
  }, [
    closeCancelInvoiceModal,
    invoiceId,
    isCancelBlockedByFinalInvoice,
    navigate,
    onConfirmCreditNote,
    openBlockCancelInvoiceActionModal,
    organization?.url,
    track,
    trackingDocumentType,
  ])

  const handleDeleteInvoiceButtonClick = useCallback(() => {
    track('XXX - Invoicing Core - Cancel invoice modal - No selected - Cancel (void) invoice clicked', {
      documentType: trackingDocumentType,
    })

    if (isCancelBlockedByFinalInvoice) {
      openBlockCancelInvoiceActionModal(BlockCancelInvoiceActionVariant.Void)
      return
    }

    handleDeleteInvoice()
  }, [
    handleDeleteInvoice,
    isCancelBlockedByFinalInvoice,
    openBlockCancelInvoiceActionModal,
    track,
    trackingDocumentType,
  ])

  const handleCancelButtonClick = useCallback(() => {
    closeCancelInvoiceModal()
  }, [closeCancelInvoiceModal])

  if (!shouldDisplayCancelInvoiceModalFooter) {
    return null
  }

  return (
    <Modal.Footer>
      <ButtonsGroup>
        <Button onClick={handleCancelButtonClick} variant="text">
          {t('cancel')}
        </Button>
        {isCreateCreditNoteAction && (
          <Button color="danger" onClick={handleCancelWithCreditNoteButtonClick}>
            {t('external_invoices.view.cancel.modal.cancel_with_credit_note')}
          </Button>
        )}
        {isDeleteInvoiceAction && (
          <Button color="danger" loading={isDeletingInvoice} onClick={handleDeleteInvoiceButtonClick}>
            {t('external_invoices.view.cancel.modal.cancel_with_void_invoice')}
          </Button>
        )}
      </ButtonsGroup>
    </Modal.Footer>
  )
}
